import billingApi from '@api/billingApi';
import DefaultPage from '@components/defaultPage';
import { LoadingIndicator } from '@components/loadingIndicator';
import ScopeIcon from '@components/scopeIcon';
import { css } from '@emotion/css';
import useAuth from '@hooks/useAuth';
import useIsMobile from '@hooks/useIsMobile';
import { useTranslator } from '@i18n';
import { Group, Text } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { createFileRoute } from '@tanstack/react-router';
import { useHandleError } from '@util/errors';
import { createUrlParam } from 'cross-state/react';
import { useMemo } from 'react';
import { Table, textFilter, type Column, type Sort } from 'schummar-table';

export const Route = createFileRoute('/profile/billing/')({
  component: BillingPage,
});

/** The month to report on, as `yyyy-MM`. Empty means the current one, which is what the backend defaults to. */
const monthUrl = createUrlParam<string>({ type: 'hash', key: 'month', defaultValue: '' });

const defaultSort: Sort[] = [{ columnId: 'name', direction: 'asc' }];

const countClasses = {
  cell: css({ justifyContent: 'flex-end' }),
  headerCell: css({ justifyContent: 'flex-end' }),
};

/**
 * One row of the table. Organizations and their locations share the shape so they can be handed to the
 * table as one flat list, with `parentOrgId` turning it back into a tree.
 */
interface BillingRow {
  id: string;
  parentOrgId?: string;
  kind: 'tenant' | 'location';
  name: string;
  accountReference?: string | null;
  externalReference?: string | null;
  deviceCountBilled: number;
  deviceCountCurrentPeriod: number;
}

function BillingPage() {
  const t = useTranslator();
  const isMobile = useIsMobile();
  const { hasClaim } = useAuth();
  const [month, setMonth] = monthUrl.useProp();

  // The backend refuses without it, so the request is not worth making - and a plain sentence reads
  // better than the error notification a 403 would raise
  const canSeeBilling = hasClaim('orgs/billing', 'any');

  // The backend hands the account an organization bills to to global callers only, so the column would
  // be an empty one for everybody else
  const canSeeAccounts = hasClaim('orgs/billing', 'global');

  // Derived from organizations, locations and devices, none of which invalidate it when they change
  const [report, reportError] = billingApi
    .report({ month: month || undefined })
    .useCache({ disabled: !canSeeBilling, updateOnMount: true });
  useHandleError(reportError);

  const items = useMemo<BillingRow[]>(
    () =>
      report?.organizations.flatMap((org): BillingRow[] => [
        {
          id: org.orgId,
          kind: 'tenant',
          name: org.name,
          accountReference: org.accountReference,
          externalReference: org.externalReference,
          deviceCountBilled: org.deviceCountBilled,
          deviceCountCurrentPeriod: org.deviceCountCurrentPeriod,
        },
        ...org.locations.map((location): BillingRow => ({
          id: `${org.orgId}/${location.locationId}`,
          parentOrgId: org.orgId,
          kind: 'location',
          name: location.name,
          externalReference: location.externalReference,
          deviceCountBilled: location.deviceCountBilled,
          deviceCountCurrentPeriod: location.deviceCountCurrentPeriod,
        })),
      ]) ?? [],
    [report],
  );

  const allColumns: Column<BillingRow, any>[] = [
    {
      id: 'name',
      header: t('billing.columns.name'),
      value: (item) => item.name,
      renderCell: (name, item) => (
        <Group gap="xs" wrap="nowrap">
          <ScopeIcon kind={item.kind} />
          <span css={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        </Group>
      ),
      width: 'minmax(20ch, 1fr)',
      filter: textFilter(),
    },
    {
      id: 'accountReference',
      header: t('billing.columns.accountReference'),
      // Only organizations are billed to an account; their locations inherit it
      value: (item) => item.accountReference ?? '',
      width: 'minmax(max-content, 1fr)',
      displaySize: 'desktop',
      filter: textFilter(),
    },
    {
      id: 'externalReference',
      header: t('billing.columns.externalReference'),
      value: (item) => item.externalReference ?? '',
      width: 'minmax(max-content, 1fr)',
      displaySize: 'desktop',
      filter: textFilter(),
    },
    {
      id: 'deviceCountCurrentPeriod',
      header: t('billing.columns.deviceCountCurrentPeriod'),
      value: (item) => item.deviceCountCurrentPeriod,
      renderCell: (count) => <span data-testid="billing-device-count-current">{count}</span>,
      width: 'max-content',
      classes: countClasses,
    },
    {
      id: 'deviceCountBilled',
      header: t('billing.columns.deviceCountBilled'),
      value: (item) => (item.kind === 'tenant' ? item.deviceCountBilled : ''),
      renderCell: (count) => <span data-testid="billing-device-count-billed">{count}</span>,
      width: 'max-content',
      classes: countClasses,
    },
  ];

  const columns = canSeeAccounts ? allColumns : allColumns.filter((column) => column.id !== 'accountReference');

  return (
    <DefaultPage
      title={t('billing.title')}
      backButton={{ to: '/profile' }}
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--mantine-spacing-md)',
        paddingBottom: 50,
      }}
    >
      <Text size="sm" c="dimmed">
        {t('billing.description')}
      </Text>

      <Group gap="sm" align="end" wrap="wrap">
        <MonthPickerInput
          label={t('billing.month.label')}
          placeholder={t('billing.month.placeholder')}
          // The picker speaks whole dates, the report speaks months
          value={month ? `${month}-01` : null}
          onChange={(value) => setMonth(value?.slice(0, 7) ?? '')}
          clearable
          css={{ minWidth: 200 }}
        />
      </Group>

      {!canSeeBilling ? (
        <Text c="dimmed">{t('billing.noPermission')}</Text>
      ) : !report ? (
        <LoadingIndicator />
      ) : items.length === 0 ? (
        <Text c="dimmed">{t('billing.empty')}</Text>
      ) : (
        <Table
          items={items}
          id="id"
          parentId="parentOrgId"
          columns={columns}
          fullWidth
          enableSelection={false}
          enableColumnSelection={!isMobile}
          enableExport={!isMobile}
          defaultSort={defaultSort}
          displaySize={{ mobile: 700, desktop: Infinity }}
          stickyHeader={{ top: 'var(--app-shell-header-height)' }}
          wrapRow={(props) => <div {...props} data-testid="billing-row" />}
        />
      )}
    </DefaultPage>
  );
}
